import { AppDispatch, RootState } from "@/services/state/store";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Text, useDisclosure, useToast } from "@chakra-ui/react";
import { getAxiosClient } from "../../services/HttpsService";
import {
  fetchConnectedSystemAuths,
  fetchConnectedSystemsById,
} from "../../services/state/connected-systems/connectedSystem-async-calls";
import {
  setSelectedConnectedSystem,
  setSelectedConnectedSystemAuth,
} from "../../services/state/connected-systems/connectedSystem";

export const useConnectedSystem = () => {
  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const { csId } = useParams();

  const { isOpen: isEditing, onToggle: onEditToggle } = useDisclosure();

  const selectedConnectedSystem = useSelector(
    (state: RootState) => state.connectedSystems.selectedConnectedSystem
  );

  const selectedConnectedSystemInitial = useSelector(
    (state: RootState) => state.connectedSystems.selectedConnectedSystemInitial
  );

  const selectedConnectedSystemAuth = useSelector(
    (state: RootState) => state.connectedSystems.selectedConnectedSystemAuth
  );

  const connectedSystemAuths = useSelector(
    (state: RootState) => state.connectedSystems.connectedSystemAuths
  );

  useEffect(() => {
    dispatch(fetchConnectedSystemsById(csId as string));
    dispatch(fetchConnectedSystemAuths());
  }, []);

  const authsOptions = connectedSystemAuths.map((auth) => ({
    id: auth.authCd,
    name: auth.authNm,
    component: <Text>{auth.authNm}</Text>,
  }));

  const onChange = (feild: "csNm" | "csDesc" | "baseUrl", value: string) => {
    if (selectedConnectedSystem) {
      const selectedConnectedSystemCopy = { ...selectedConnectedSystem };
      selectedConnectedSystemCopy[feild] = value;
      dispatch(setSelectedConnectedSystem(selectedConnectedSystemCopy));
    }
  };

  const onAuthValueChange = (propCd: string, propValue: string) => {
    if (selectedConnectedSystem) {
      const selectedConnectedSystemCopy = { ...selectedConnectedSystem };
      selectedConnectedSystemCopy.systemAuth = [
        ...selectedConnectedSystem.systemAuth,
      ];
      const auth = selectedConnectedSystemCopy.systemAuth.findIndex(
        (auth) => auth.authPropCd === propCd
      );

      if (auth !== -1) {
        const authCopy = { ...selectedConnectedSystemCopy.systemAuth[auth] };
        authCopy.authPropVal = propValue;
        selectedConnectedSystemCopy.systemAuth[auth] = authCopy;
      } else
        selectedConnectedSystemCopy.systemAuth.push({
          id: 0,
          csUuid: selectedConnectedSystemCopy.uuid,
          authPropCd: propCd,
          authPropVal: propValue,
          isActive: "Y",
          createdBy: null,
          createdOn: "",
          updatedBy: null,
          updatedOn: "",
        });

      dispatch(setSelectedConnectedSystem(selectedConnectedSystemCopy));
    }
  };

  const onAuthChange = (authCd: string) => {
    const auth = connectedSystemAuths.find((it) => it.authCd === authCd);
    console.log(auth);
    if (auth && selectedConnectedSystem) {
      dispatch(setSelectedConnectedSystemAuth(auth));
      const selectedConnectedSystemCopy = { ...selectedConnectedSystem };
      selectedConnectedSystemCopy.auth = auth;
      selectedConnectedSystemCopy.systemAuth = auth.authProps.map((prop) => ({
        id: 0,
        csUuid: selectedConnectedSystemCopy.uuid,
        authPropCd: prop.authPropCd,
        authPropVal: "",
        isActive: "Y",
        createdBy: null,
        createdOn: "",
        updatedBy: null,
        updatedOn: "",
      }));
      dispatch(setSelectedConnectedSystem(selectedConnectedSystemCopy));
    }
  };

  const isEdited =
    JSON.stringify(selectedConnectedSystem) !==
    JSON.stringify(selectedConnectedSystemInitial);

  const onSave = async () => {
    let hasError = false;

    ["csNm", "baseUrl"].forEach((feild) => {
      // @ts-ignore
      if (selectedConnectedSystem && selectedConnectedSystem[feild] === "") {
        toast({
          title: "Error saving connected system",
          description:
            feild === "csNm"
              ? "Connected system name cannot be empty"
              : "Base URL cannot be empty",
          status: "error",
          position: "top",
          duration: 3000,
          isClosable: true,
        });
        hasError = true;
      }
    });

    const urlRegex =
      /^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}|localhost|\d{1,3}(\.\d{1,3}){3})(:\d+)?(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;
    if (
      selectedConnectedSystem &&
      !urlRegex.test(selectedConnectedSystem["baseUrl"])
    ) {
      toast({
        title: "Error saving connected system",
        description: "Base URL is not valid",
        status: "error",
        position: "top",
        duration: 3000,
        isClosable: true,
      });
      hasError = true;
    }

    if (hasError) return;

    const data = {
      id: selectedConnectedSystem?.id,
      uuid: selectedConnectedSystem?.uuid,
      csNm: selectedConnectedSystem?.csNm,
      appUuid: selectedConnectedSystem?.app.uuid,
      csDesc: selectedConnectedSystem?.csDesc,
      baseUrl: selectedConnectedSystem?.baseUrl,
      authCd: selectedConnectedSystem?.auth?.authCd,
      systemAuth: selectedConnectedSystem?.systemAuth.map((auth) => ({
        id: auth.id,
        csUuid: auth.csUuid,
        authPropCd: auth.authPropCd,
        authPropVal: auth.authPropVal,
      })),
    };

    const response = await getAxiosClient().post(
      "/api/conn-systems/update-auth-props",
      data
    );

    dispatch(fetchConnectedSystemsById(csId as string));

    if (isEditing) onEditToggle();
  };

  return {
    selectedConnectedSystem,
    selectedConnectedSystemAuth,
    isEditing,
    isEdited,
    authsOptions,
    onEditToggle,
    onAuthChange,
    onAuthValueChange,
    onChange,
    onSave,
  };
};
